import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Zap, Receipt, AlertCircle } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { PageHeader, GlassCard, ProgressBar, Spinner, Chip, Disclaimer } from "../components/ui";
import { PLANS } from "../constants";

const ORDER = ["free", "pro", "business", "enterprise"];

export default function Billing() {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.get("/subscription").then(({ data }) => setData(data));
    api.get("/subscription/invoices").then(({ data }) => setInvoices(data));
  };
  useEffect(() => { load(); }, []);

  const change = async (plan) => {
    const target = PLANS.find((p) => p.name.toLowerCase() === plan);
    if (target?.to?.startsWith("mailto")) { window.location.href = target.to; return; }
    setBusy(plan);
    try {
      if (plan === "free") {
        const { data: res } = await api.post("/subscription/change", { plan, billing_cycle: yearly ? "yearly" : "monthly" });
        toast.success(res.note || "Subscription updated");
        await refreshUser();
        load();
        setBusy(null);
        return;
      }
      // Existing paid Stripe subscriber changing plan → manage via Stripe portal (proration handled by Stripe).
      if (data?.billing?.is_stripe && data?.subscription?.plan !== "free") {
        await openPortal();
        setBusy(null);
        return;
      }
      const { data: res } = await api.post("/payments/checkout", {
        plan, billing_cycle: yearly ? "yearly" : "monthly", origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  const openPortal = async () => {
    try {
      const { data: res } = await api.post("/payments/portal", { return_url: window.location.origin });
      window.open(res.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal");
    }
  };

  const resume = async () => {
    try {
      await api.post("/subscription/resume");
      toast.success("Subscription resumed — it will continue as normal");
      await refreshUser();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resume subscription");
    }
  };

  const cancel = async () => {
    try {
      await api.post("/subscription/cancel");
      toast.success("Cancellation scheduled — you keep access until the period ends");
      await refreshUser();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    }
  };

  if (!data) return <Spinner />;
  const { subscription: sub, usage, limits } = data;

  const usageRows = [
    { key: "ai_questions", label: "AI Coach Questions" },
    { key: "report_analyses", label: "Report Analyses" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader label="Account" title="Subscription & Billing"
        subtitle="Manage your plan, usage, and invoices. Secure checkout powered by Stripe (test mode)." />

      <div className="grid lg:grid-cols-3 gap-5">
        <GlassCard className="p-6">
          <div className="flex items-center gap-2 mb-3"><Zap size={17} className="text-blue-300" /><h3 className="cc-heading text-lg font-500 text-white">Current Plan</h3></div>
          <div className="cc-heading text-3xl font-700 text-white capitalize">{sub.plan}</div>
          <div className="mt-1 flex items-center gap-2">
            <Chip tone={sub.status === "active" ? "green" : "amber"}>{sub.status}</Chip>
            <Chip>{sub.billing_cycle}</Chip>
          </div>
          <p className="text-sm text-slate-400 mt-3">
            {sub.cancel_at_period_end ? "Access until" : "Renews"} {new Date(sub.current_period_end).toLocaleDateString()}
          </p>
          {sub.cancel_at_period_end ? (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-sm text-amber-300" data-testid="cancel-scheduled">
                <AlertCircle size={15} /> Your {sub.plan} plan will end on {new Date(sub.current_period_end).toLocaleDateString()}
              </div>
              <p className="text-xs text-slate-500 mt-1">You keep full access until then.</p>
              <button onClick={resume} className="mt-3 rounded-xl cc-btn-primary text-white text-sm px-4 py-2 font-500" data-testid="resume-sub">Resume subscription</button>
            </div>
          ) : sub.plan !== "free" && (
            <button onClick={cancel} className="mt-4 text-sm text-red-300 hover:text-red-200" data-testid="cancel-sub">Cancel subscription</button>
          )}
          {data?.billing?.has_stripe_customer && (
            <button onClick={openPortal} className="mt-4 block w-full rounded-xl border border-white/12 text-white cc-nav-item py-2.5 text-sm font-500" data-testid="manage-billing">Manage billing</button>
          )}
        </GlassCard>

        <GlassCard className="p-6 lg:col-span-2">
          <h3 className="cc-heading text-lg font-500 text-white mb-4">Usage This Month</h3>
          <div className="space-y-4">
            {usageRows.map((r) => {
              const used = usage[r.key] || 0;
              const lim = limits[r.key];
              const pct = lim === -1 ? 100 : Math.min(100, (used / Math.max(1, lim)) * 100);
              return (
                <div key={r.key}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-slate-300">{r.label}</span>
                    <span className="text-slate-400">{used} / {lim === -1 ? "Unlimited" : lim}</span>
                  </div>
                  <ProgressBar value={lim === -1 ? 12 : pct} />
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-4">Free plan limits reset monthly. Upgrade for unlimited AI usage.</p>
        </GlassCard>
      </div>

      {/* Plan chooser */}
      <div className="flex items-center justify-center gap-3">
        <span className={`text-sm ${!yearly ? "text-white" : "text-slate-500"}`}>Monthly</span>
        <button onClick={() => setYearly((v) => !v)} className="relative h-6 w-11 rounded-full cc-surface-2 border border-white/10" data-testid="billing-cycle-toggle">
          <motion.span className="absolute top-0.5 h-5 w-5 rounded-full cc-gradient" animate={{ left: yearly ? "22px" : "2px" }} transition={{ type: "spring", stiffness: 500, damping: 30 }} />
        </button>
        <span className={`text-sm ${yearly ? "text-white" : "text-slate-500"}`}>Yearly <span className="text-emerald-400 text-xs">(save 20%)</span></span>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {PLANS.map((p) => {
          const key = p.name.toLowerCase();
          const isCurrent = sub.plan === key;
          const price = p.price === null ? null : yearly ? Math.round(p.price * 12 * 0.8) : p.price;
          return (
            <GlassCard key={p.name} className={`p-6 flex flex-col ${p.popular ? "ring-1 ring-blue-500/50" : ""}`} data-testid={`billing-plan-${key}`}>
              <h3 className="cc-heading text-lg font-600 text-white">{p.name}</h3>
              <div className="mt-2 cc-heading text-3xl font-700 text-white">
                {price === null ? "Custom" : `$${price}`}
                {price !== null && <span className="text-slate-500 text-sm font-400">/{yearly ? "yr" : "mo"}</span>}
              </div>
              <button
                onClick={() => change(key)}
                disabled={isCurrent || busy === key}
                data-testid={`billing-select-${key}`}
                className={`mt-4 rounded-xl py-2.5 text-sm font-500 ${isCurrent ? "border border-white/10 text-slate-500" : p.popular ? "cc-btn-primary text-white" : "border border-white/12 text-white cc-nav-item"}`}>
                {isCurrent ? "Current Plan" : busy === key ? "Processing…" : ORDER.indexOf(key) < ORDER.indexOf(sub.plan) ? "Downgrade" : p.cta}
              </button>
              <ul className="mt-5 space-y-2">
                {p.features.slice(0, 6).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-slate-400"><Check size={13} className="mt-0.5 shrink-0 text-blue-400" />{f}</li>
                ))}
              </ul>
            </GlassCard>
          );
        })}
      </div>

      <GlassCard className="p-6">
        <h3 className="cc-heading text-lg font-500 text-white mb-4 flex items-center gap-2"><Receipt size={17} className="text-blue-300" /> Invoices</h3>
        {invoices.length === 0 ? (
          <p className="text-sm text-slate-500">No invoices yet.</p>
        ) : (
          <div className="space-y-2">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between rounded-lg cc-surface border border-white/[0.05] px-4 py-3" data-testid="invoice-row">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-white font-mono">{inv.number}</span>
                  <Chip tone="green">{inv.status}</Chip>
                  <span className="text-xs text-slate-500 capitalize">{inv.plan} · {inv.cycle}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-300">${inv.amount}</span>
                  <span className="text-xs text-slate-500">{new Date(inv.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassCard>

      <Disclaimer text="Secure payments are processed by Stripe (test mode). Use test card 4242 4242 4242 4242, any future expiry and any CVC. Downgrade to Free or cancel anytime." />
    </div>
  );
}
